import React from 'react';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  MoreVertical,
  Trash2,
  Edit
} from 'lucide-react';
import GoalProgressBar from './GoalProgressBar';
import { goalService } from '../../services/goalService';

/**
 * GoalCard - Individual goal display component
 *
 * Props:
 * - goal: Goal object from API
 * - onEdit: function(goal) - Called when edit is clicked
 * - onDelete: function(goalId) - Called when delete is clicked
 * - compact: boolean - Use compact layout
 * - showActions: boolean - Show edit/delete actions
 */
const GoalCard = ({
  goal,
  onEdit,
  onDelete,
  compact = false,
  showActions = true,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);

  // Direction icon
  const DirectionIcon = () => {
    switch (goal.direction) {
      case 'maximize':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'minimize':
        return <TrendingDown className="h-4 w-4 text-blue-600" />;
      case 'target':
        return <Target className="h-4 w-4 text-purple-600" />;
      default:
        return null;
    }
  };

  // Status badge
  const StatusBadge = () => {
    const statusConfig = {
      active: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Active' },
      achieved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Achieved' },
      missed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Missed' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Cancelled' },
    };

    const config = statusConfig[goal.status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Format dates
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate days remaining
  const daysRemaining = () => {
    const end = new Date(goal.period_end);
    const today = new Date();
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const days = daysRemaining();
  const isOverdue = days < 0 && goal.status === 'active';
  const isCloseToDeadline = days <= 7 && days >= 0 && goal.status === 'active';

  // On track calculation
  const isOnTrack = () => {
    const totalDays = (new Date(goal.period_end) - new Date(goal.period_start)) / (1000 * 60 * 60 * 24);
    const elapsedDays = (new Date() - new Date(goal.period_start)) / (1000 * 60 * 60 * 24);
    if (totalDays <= 0) return true;

    const expectedProgress = (elapsedDays / totalDays) * 100;

    if (goal.direction === 'minimize') {
      return Number(goal.current_value) <= Number(goal.target_value);
    }

    return Number(goal.progress_percentage) >= expectedProgress * 0.8;
  };

  if (compact) {
    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <DirectionIcon />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{goal.title}</p>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span>{goalService.formatMetricValue(goal.current_value, goal.metric_type)}</span>
              <span>/</span>
              <span>{goalService.formatMetricValue(goal.target_value, goal.metric_type)}</span>
            </div>
          </div>
        </div>
        <div className="w-24 ml-4">
          <GoalProgressBar
            progress={Number(goal.progress_percentage)}
            direction={goal.direction}
            isOnTrack={isOnTrack()}
            size="sm"
            showLabel={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${
              goal.direction === 'maximize' ? 'bg-green-100' :
              goal.direction === 'minimize' ? 'bg-blue-100' : 'bg-purple-100'
            }`}>
              <DirectionIcon />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{goal.title}</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {goalService.getPeriodLabel(goal.period)} Goal
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <StatusBadge />

            {showActions && (onEdit || onDelete) && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <MoreVertical className="h-5 w-5 text-gray-400" />
                </button>

                {showMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowMenu(false)}
                    />
                    <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      {onEdit && (
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            onEdit(goal);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            onDelete(goal.id);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="p-4">
        <div className="mb-4">
          <GoalProgressBar
            progress={Number(goal.progress_percentage)}
            direction={goal.direction}
            isOnTrack={isOnTrack()}
            size="md"
            showLabel={true}
          />
        </div>

        {/* Metrics Display */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-1">
            <span className="text-gray-500">Current:</span>
            <span className="font-semibold text-gray-900">
              {goalService.formatMetricValue(goal.current_value, goal.metric_type)}
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-gray-500">Target:</span>
            <span className="font-semibold text-gray-900">
              {goalService.formatMetricValue(goal.target_value, goal.metric_type)}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-1 text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>
              {formatDate(goal.period_start)} - {formatDate(goal.period_end)}
            </span>
          </div>

          <div className={`flex items-center space-x-1 ${
            isOverdue ? 'text-red-600' :
            isCloseToDeadline ? 'text-yellow-600' : 'text-gray-500'
          }`}>
            {isOverdue ? (
              <>
                <AlertTriangle className="h-4 w-4" />
                <span>{Math.abs(days)} days overdue</span>
              </>
            ) : days === 0 ? (
              <>
                <Clock className="h-4 w-4" />
                <span>Due today</span>
              </>
            ) : (
              <>
                <Clock className="h-4 w-4" />
                <span>{days} days left</span>
              </>
            )}
          </div>
        </div>

        {/* On Track Indicator */}
        {goal.status === 'active' && (
          <div className={`mt-2 flex items-center space-x-1 text-xs ${
            isOnTrack() ? 'text-green-600' : 'text-yellow-600'
          }`}>
            {isOnTrack() ? (
              <>
                <CheckCircle className="h-3 w-3" />
                <span>On track</span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-3 w-3" />
                <span>Needs attention</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalCard;
